// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import axios from 'axios';

export const BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const ACCESS_KEY = 'access_token';
export const REFRESH_KEY = 'refresh_token';

const REFRESH_LOCK_KEY = 'refresh_lock';
export const REFRESH_LOCK_TTL_MS = 30_000; // 30s

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10_000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request — attach access token ─────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response — queue on 401, let AuthContext refresh ──────────────
// AuthContext owns the refresh lifecycle and setUser().
// The interceptor only queues requests and retries — it does NOT
// attempt its own refresh to avoid state desync.

let isRefreshing = false;
let refreshQueue = [];

// Auth endpoints answer 401 for bad credentials, not for an expired session.
// Refreshing can't fix those, so their errors go straight back to the caller.
const NO_REFRESH_PATHS = ['/api/token/', '/api/token/refresh/', '/api/register/'];
const isAuthRequest = (config) =>
  NO_REFRESH_PATHS.some((path) => config?.url?.startsWith(path));

const processQueue = (error, token = null) => {
  refreshQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  refreshQueue = [];
};

// Called by AuthContext after a successful refresh
export function notifyRefreshSuccess(newToken) {
  processQueue(null, newToken);
  isRefreshing = false;
}

// Called by AuthContext when refresh fails
export function notifyRefreshFailure(error) {
  processQueue(error, null);
  isRefreshing = false;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (
      error.response?.status !== 401 ||
      original._retry ||
      isAuthRequest(original) ||
      !localStorage.getItem(REFRESH_KEY) // no session to refresh
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue this request — will be retried once AuthContext refreshes
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    // Signal AuthContext to refresh (via storage event is too slow —
    // dispatch a custom event on the window instead)
    window.dispatchEvent(
      new CustomEvent('auth:refresh-required', {
        detail: { original },
      }),
    );

    // Queue this request — AuthContext will call notifyRefreshSuccess/Failure
    return new Promise((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    }).then((token) => {
      original.headers.Authorization = `Bearer ${token}`;
      return api(original);
    });
  },
);

// ── Cross-tab refresh lock helpers ────────────────────────────────
export function tryAcquireRefreshLock(tabId) {
  try {
    const now = Date.now();
    const current = localStorage.getItem(REFRESH_LOCK_KEY);
    if (current) {
      const parsed = JSON.parse(current);
      if (parsed && now - parsed.timestamp < REFRESH_LOCK_TTL_MS) {
        if (parsed.owner !== tabId) return false;
      }
    }
    localStorage.setItem(
      REFRESH_LOCK_KEY,
      JSON.stringify({ owner: tabId, timestamp: now }),
    );
    return true;
  } catch {
    return true;
  }
}

export function releaseRefreshLock(tabId) {
  try {
    const current = localStorage.getItem(REFRESH_LOCK_KEY);
    if (!current) return;
    const parsed = JSON.parse(current);
    if (parsed?.owner === tabId) localStorage.removeItem(REFRESH_LOCK_KEY);
  } catch {
    // noop
  }
}

export default api;
